import { CompositeLayer } from '@deck.gl/core';
import { PolygonLayer, LineLayer } from '@deck.gl/layers';
import spritesMapping from '../../assets/static-layers/sprites-output/sprites-info.json';
import staticLayersSprite from '../../assets/static-layers/sprites-output/sprites_2x.png';
import getStaticSpritesIcon from '../StaticLayersGL/getStaticSpritesIcon';
import { default as IconLayer } from './RSOSIconLayer';

// eslint-disable-next-line
const ICON_MAPPING = {
  staticActive: { x: 0, y: 0, width: 64, height: 64, mask: false },
  staticDefault: { x: 0, y: 64, width: 64, height: 64, mask: false },
};

const MARKER_SIZES = {
  sizeScale: 130, // this is the factor to scale the icon.
  sizeMinPixels: 30, // when zoomed out, the max height of the icon.
  sizeMaxPixels: 45, // when zoomed in, the max height of the icon.
};

class IconPolygonLayer extends CompositeLayer {
  initializeState() {
    super.initializeState();
    this.state.isClicked = false;
  }

  onHover(info, pickingEvent) {
    // This bugfix suppressed the first hover after a click which causes
    // features like popovers to close immediately after a click.
    if (this.state.isClicked) {
      this.state.isClicked = false;
      return;
    }
    super.onHover(info, pickingEvent);
  }

  onClick(info, pickingEvent) {
    this.state.isClicked = true;
    super.onClick(info, pickingEvent);
  }

  renderLayers() {
    const { data, id, activeMarkerId, visible, filled } = this.props;
    const sortedData = data
      .slice()
      .sort((a, b) => (a.id === activeMarkerId ? 1 : -1))
      .reverse();

    // Returns the boundary around a MultiPolygon by extracting its outermost
    // coordinates (these are the first coordinates in the array).
    const extractCoordinatesFromMultiPolygon = multiPolygon => {
      return multiPolygon.flatMap(polygon => {
        return polygon[0];
      });
    };

    // This polygon is optional, but ensure that when the polygon has a fill,
    // that there's a reliable outline rendered around it.
    const activePolygonData = sortedData
      .filter(d => d.id === activeMarkerId)
      .flatMap(d =>
        d.shape.features
          .filter(
            f =>
              f.geometry.type === 'MultiPolygon' ||
              f.geometry.type === 'Polygon',
          )
          .flatMap(f => {
            if (f.geometry.type === 'Polygon') {
              return [f.geometry.coordinates[0]];
            } else if (f.geometry.type === 'MultiPolygon') {
              return extractCoordinatesFromMultiPolygon(f.geometry.coordinates);
            }
            return [];
          })
          .flatMap((coords, i, all) =>
            i < all.length - 1
              ? [{ coords: [coords, all[i + 1]], status: d?.status }]
              : [],
          ),
      );
    return [
      new IconLayer(
        this.getSubLayerProps({
          id: `${id}-iconLayer`,
          data: sortedData,
          getPosition: d => d?.center?.coordinates,
          pickable: true,
          autoHighlight: true,
          highlightColor: [0, 0, 0, 15],
          iconAtlas: staticLayersSprite,
          iconMapping: spritesMapping,
          sizeUnits: 'meters',
          ...MARKER_SIZES,
          getIcon: d => {
            const iconState = d.id === activeMarkerId ? 'active' : 'default';
            const result = getStaticSpritesIcon(id, iconState, d);
            return result;
          },
        }),
      ),

      new LineLayer(
        this.getSubLayerProps({
          id: `${id}-lineLayer`,
          data: activePolygonData,
          pickable: false,
          getSourcePosition: d => d.coords[0],
          getTargetPosition: d => d.coords[1],
          getColor: d =>
            d.status === 'active' ? [200, 0, 0, 100] : [36, 179, 243, 200],
          getWidth: 2,
          visible,
        }),
      ),

      new PolygonLayer(
        this.getSubLayerProps({
          id: `${id}-polygonLayer`,
          data: sortedData,
          getPolygon: d => {
            return d?.shape?.features[0]?.geometry?.coordinates[0];
          },
          pickable: true,
          autoHighlight: true,
          filled,
          lineWidthMinPixels: 2,
          highlightColor: d =>
            d?.status === 'active' ? [200, 0, 0, 100] : [200, 200, 200, 40],
          getLineColor: d =>
            d?.status === 'active'
              ? [200, 0, 0, 200]
              : d.id === activeMarkerId
              ? [36, 179, 243, 200]
              : [90, 90, 90, 200],
          getFillColor: d =>
            d?.status === 'active'
              ? [200, 0, 0, 50]
              : d.id === activeMarkerId
              ? [36, 179, 243, 20]
              : [90, 90, 90, 0],
          getLineWidth: 2,
        }),
      ),
    ];
  }
}

IconPolygonLayer.layerName = 'IconPolygonLayer';
IconPolygonLayer.defaultProps = { visible: true, data: [] };

/**
 * This layer is the same as IconPolygonLayer, but the order of the layers is
 * reversed. This is used by the DeckOverlay to reverse layer order for map
 * providers that have different ordering conventions (like GMaps).
 */
export class IconPolygonLayerReversed extends IconPolygonLayer {
  renderLayers() {
    const layers = super.renderLayers();
    return layers.reverse();
  }
}

IconPolygonLayerReversed.layerName = 'IconPolygonLayerReversed';
IconPolygonLayerReversed.defaultProps = { visible: true, data: [] };

export default IconPolygonLayer;
